import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import axios from "axios";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Bell, Trash2, Check, Clock, Users } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WaitlistView = () => {
  const { t } = useTranslation();
  const [waitlist, setWaitlist] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWaitlist();
  }, [selectedDate]);

  const fetchWaitlist = async () => {
    try {
      setLoading(true);
      const dateStr = selectedDate.toISOString().split('T')[0];
      const response = await axios.get(`${API}/waitlist?date=${dateStr}`);
      setWaitlist(response.data);
    } catch (error) {
      console.error("Error fetching waitlist:", error);
      toast.error("Error loading waitlist");
    } finally {
      setLoading(false);
    }
  };

  const notifyGuest = async (entry) => {
    try {
      await axios.post(`${API}/waitlist/${entry.id}/notify`);
      toast.success(`Notificatie verzonden naar ${entry.name}`);
      fetchWaitlist(); // Refresh to show notified status
    } catch (error) {
      console.error("Error notifying guest:", error);
      toast.error("Fout bij verzenden notificatie");
    }
  };

  const deleteEntry = async (entry) => {
    if (!window.confirm(`Weet u zeker dat u ${entry.name} van de wachtlijst wilt verwijderen?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/waitlist/${entry.id}`);
      setWaitlist(waitlist.filter(w => w.id !== entry.id));
      toast.success("Wachtlijst inschrijving verwijderd");
    } catch (error) {
      console.error("Error deleting entry:", error);
      toast.error("Fout bij verwijderen");
    }
  };

  const convertToReservation = async (entry) => {
    try {
      // Create a reservation from waitlist entry
      await axios.post(`${API}/reservations`, {
        name: entry.name,
        email: entry.email,
        phone: entry.phone,
        date: entry.date,
        time: entry.time,
        party_size: entry.party_size,
        special_requests: "",
        allergies: "",
        language: entry.language || "nl",
        source: "waitlist"
      });
      
      // Delete from waitlist
      await axios.delete(`${API}/waitlist/${entry.id}`);
      setWaitlist(waitlist.filter(w => w.id !== entry.id));
      
      toast.success(`Reservering aangemaakt voor ${entry.name}`);
    } catch (error) {
      console.error("Error converting to reservation:", error);
      const errorMsg = error.response?.data?.detail || "Fout bij aanmaken reservering";
      toast.error(errorMsg);
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden" data-testid="waitlist-view">
      <div className="p-6 border-b border-white/10">
        <h2 className="text-2xl font-serif text-gray-300" data-testid="waitlist-title">{t('dashboard.waitlist')}</h2>
        <p className="text-sm text-gray-400">Beheer wachtlijst inschrijvingen en stuur notificaties</p>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-[#1A1A1A] border border-white/10 rounded-lg p-4">
            <h3 className="text-lg font-serif text-brand-beige mb-4">Selecteer Datum</h3>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              className="rounded-md border border-white/20"
              data-testid="waitlist-calendar"
            />
            
            <div className="mt-4 p-3 bg-[#222222] rounded-lg">
              <p className="text-sm text-gray-400">Totaal op wachtlijst:</p>
              <p className="text-2xl font-semibold text-brand-gold">{waitlist.length}</p>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-4">
            {loading ? (
              <div className="text-center py-8 text-gray-300">Laden...</div>
            ) : waitlist.length === 0 ? (
              <div className="bg-[#1A1A1A] border border-white/10 rounded-lg p-8 text-center">
                <Clock className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400" data-testid="no-waitlist">Geen wachtlijst inschrijvingen voor deze datum</p>
              </div>
            ) : (
              waitlist.map((entry, index) => (
                <div 
                  key={entry.id} 
                  className={`bg-[#1A1A1A] border rounded-lg p-4 transition-colors ${
                    entry.notified ? 'border-green-500/30' : 'border-white/10 hover:border-white/20'
                  }`}
                  data-testid={`waitlist-entry-${index}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-gray-200" data-testid={`waitlist-name-${index}`}>
                          {entry.name}
                        </h4>
                        {entry.notified && (
                          <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">
                            Genotificeerd
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-400" data-testid={`waitlist-email-${index}`}>{entry.email}</p>
                      <p className="text-sm text-gray-400">{entry.phone}</p>
                    </div>
                    
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-gray-300">
                        <Users className="w-4 h-4" />
                        <span>{entry.party_size} personen</span>
                      </div>
                      <p className="text-lg text-brand-gold font-semibold" data-testid={`waitlist-time-${index}`}>
                        {entry.time}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 mt-4 pt-4 border-t border-white/10">
                    <Button
                      size="sm"
                      onClick={() => notifyGuest(entry)}
                      disabled={entry.notified}
                      className={entry.notified 
                        ? "bg-gray-700 text-gray-400 cursor-not-allowed" 
                        : "bg-blue-600 hover:bg-blue-700 text-white"
                      }
                      data-testid={`notify-btn-${index}`}
                    >
                      <Bell className="w-4 h-4 mr-1" />
                      {entry.notified ? "Genotificeerd" : "Notificeren"}
                    </Button>
                    
                    <Button
                      size="sm"
                      onClick={() => convertToReservation(entry)}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`convert-btn-${index}`}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Omzetten naar reservering
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deleteEntry(entry)}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      data-testid={`delete-waitlist-btn-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaitlistView;
